import React, { useEffect, useState } from 'react';
import { Link, useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft, Calendar, Clock, Users, CheckCircle2, BookOpen, Loader2, ShieldCheck, ChevronRight } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { fetchExam, fetchAuthor } from '../lib/api';
import Seo from '../components/Seo';
import { buildExamSchema } from '../lib/schema';
import NotFound from './NotFound';

const ExamDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [state, setState] = useState(undefined); // undefined | null | {kind, data, parent, subpages}
  const [author, setAuthor] = useState(null);

  useEffect(() => {
    setState(undefined);
    setAuthor(null);
    fetchExam(id)
      .then((res) => {
        setState(res);
        const src = res.kind === 'parent' ? res.parent : res.data;
        if (src?.author_id) fetchAuthor(src.author_id).then(setAuthor).catch(() => {});
      })
      .catch(() => setState(null));
  }, [id]);

  if (state === undefined) return <div className="min-h-screen grid place-items-center bg-slate-50"><Loader2 className="w-6 h-6 animate-spin text-teal-600" /></div>;
  if (state === null) return <NotFound />;

  const { kind, data, parent, subpages } = state;
  const isSub = kind === 'sub';
  const activeSlug = isSub ? data.slug : parent.slug;

  const seoSrc = data;
  const heroImg = parent.image || parent.og_image;

  return (
    <div className="bg-slate-50 min-h-screen">
      <Seo
        title={seoSrc.meta_title || `${isSub ? data.tab_label + ' - ' : ''}${parent.name} | Learndunia`}
        description={seoSrc.meta_description || parent.description}
        keywords={seoSrc.meta_keywords}
        image={seoSrc.og_image || heroImg}
        canonical={seoSrc.canonical_url || `https://learndunia.com/exams/${activeSlug}`}
        jsonLd={buildExamSchema(parent)}
      />

      <div className="relative h-56 md:h-72 overflow-hidden">
        <img src={heroImg} alt={parent.name} className="w-full h-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-t from-slate-900 via-slate-900/70 to-transparent" />
        <div className="absolute inset-x-0 top-4 max-w-7xl mx-auto px-4">
          <button onClick={() => navigate('/exams')} className="inline-flex items-center gap-1 text-white/90 hover:text-white text-sm bg-black/30 backdrop-blur px-3 py-1.5 rounded-full">
            <ArrowLeft className="w-4 h-4" /> All exams
          </button>
        </div>
        <div className="absolute bottom-6 left-0 right-0 max-w-7xl mx-auto px-4 text-white">
          <div className="flex items-center gap-2 text-xs text-white/70 mb-2">
            <Link to="/exams" className="hover:text-white">Exams</Link>
            <ChevronRight className="w-3 h-3" />
            <Link to={`/exams/${parent.slug}`} className="hover:text-white">{parent.name}</Link>
            {isSub && (<><ChevronRight className="w-3 h-3" /><span className="text-white">{data.tab_label}</span></>)}
          </div>
          <Badge className="bg-orange-500 hover:bg-orange-500 mb-2">{parent.level}</Badge>
          <h1 className="text-3xl md:text-5xl font-bold" style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }}>
            {isSub ? `${parent.name}: ${data.tab_label}` : parent.name}
          </h1>
          {!isSub && <p className="text-white/80">{parent.full}</p>}
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 py-8">
        {/* Stats card */}
        {!isSub && (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            {[
              { i: Calendar, l: 'Next Date', v: parent.nextDate },
              { i: Clock, l: 'Frequency', v: parent.frequency },
              { i: Users, l: 'Registrations', v: parent.registrations },
              { i: BookOpen, l: 'Mode', v: parent.mode },
            ].map((s, i) => (
              <div key={i} className="bg-white border border-slate-200 rounded-xl p-4 flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-teal-100 grid place-items-center"><s.i className="w-5 h-5 text-teal-700" /></div>
                <div>
                  <div className="text-[11px] uppercase text-slate-500">{s.l}</div>
                  <div className="font-semibold text-slate-900">{s.v}</div>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Tabs */}
        <div className="mt-6 bg-white border border-slate-200 rounded-xl p-1 overflow-x-auto flex gap-1">
          <Link to={`/exams/${parent.slug}`}
            className={`px-4 py-2 rounded-lg text-sm font-medium whitespace-nowrap transition-colors ${!isSub ? 'bg-teal-600 text-white' : 'text-slate-700 hover:bg-slate-100'}`}>
            Overview
          </Link>
          {subpages.map((sp) => (
            <Link key={sp.id} to={`/exams/${sp.slug}`}
              className={`px-4 py-2 rounded-lg text-sm font-medium whitespace-nowrap transition-colors ${isSub && sp.slug === activeSlug ? 'bg-teal-600 text-white' : 'text-slate-700 hover:bg-slate-100'}`}>
              {sp.tab_label}
            </Link>
          ))}
        </div>

        {/* Content */}
        <div className="mt-6 grid lg:grid-cols-3 gap-6">
          <div className="lg:col-span-2 bg-white border border-slate-200 rounded-2xl p-6">
            {isSub ? (
              <>
                <h2 className="text-2xl font-bold text-slate-900" style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }}>{data.tab_label}</h2>
                {data.content ? (
                  <div className="prose prose-slate max-w-none mt-4" dangerouslySetInnerHTML={{ __html: data.content }} />
                ) : (
                  <p className="text-slate-500 mt-3">Content coming soon.</p>
                )}
              </>
            ) : (
              <>
                <h2 className="text-xl font-bold text-slate-900" style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }}>About {parent.name}</h2>
                <p className="mt-3 text-slate-700 leading-relaxed">{parent.description}</p>
                {parent.content && <div className="prose prose-slate max-w-none mt-5" dangerouslySetInnerHTML={{ __html: parent.content }} />}
                <div className="mt-5 space-y-2">
                  {['Conducted by an autonomous national body', 'Multiple exam centres across India', 'Merit list published within a month', 'Counselling and seat allotment follows scorecard'].map((h) => (
                    <div key={h} className="flex items-start gap-2 text-sm text-slate-700">
                      <CheckCircle2 className="w-4 h-4 text-teal-600 mt-0.5" /> {h}
                    </div>
                  ))}
                </div>
              </>
            )}

            {author && (
              <div className="mt-8 pt-6 border-t border-slate-100 flex items-center gap-3">
                <img src={author.avatar} alt={author.name} className="w-12 h-12 rounded-full object-cover" />
                <div>
                  <div className="text-xs text-slate-500 inline-flex items-center gap-1">
                    Reviewed by {author.is_fact_checker && <ShieldCheck className="w-3 h-3 text-teal-600" />}
                  </div>
                  <Link to={`/author/${author.slug}`} className="font-semibold text-slate-900 hover:text-teal-700">{author.name}</Link>
                  <div className="text-xs text-slate-500">{author.designation}</div>
                </div>
              </div>
            )}
          </div>

          <aside className="space-y-4">
            <div className="bg-gradient-to-br from-orange-500 to-orange-600 text-white rounded-2xl p-5">
              <h3 className="font-bold text-lg" style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }}>Application Alerts</h3>
              <p className="text-orange-50 text-sm mt-1">Get notified when applications open.</p>
              <div className="mt-4 space-y-2">
                <input placeholder="Email" className="w-full px-3 py-2.5 rounded-lg text-slate-900 text-sm outline-none" />
                <Button className="w-full bg-slate-900 hover:bg-slate-800 text-white">Notify Me</Button>
              </div>
            </div>
            <div className="bg-white border border-slate-200 rounded-2xl p-5">
              <h3 className="font-semibold text-slate-900 mb-3" style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }}>Quick Links</h3>
              <div className="space-y-1">
                {subpages.slice(0, 6).map((sp) => (
                  <Link key={sp.id} to={`/exams/${sp.slug}`} className="block px-3 py-2 rounded-lg text-sm text-slate-700 hover:bg-teal-50 hover:text-teal-700">
                    {sp.tab_label} <ChevronRight className="w-3 h-3 inline float-right mt-1" />
                  </Link>
                ))}
              </div>
            </div>
          </aside>
        </div>
        <div className="h-16" />
      </div>
    </div>
  );
};

export default ExamDetail;
